// Form fields as the design draws them: label above, help under it, the error next to the field
// in plain Swedish, and a summary at the top that links to each field (WCAG 3.3.1, 3.3.3).
import { useEffect, useRef, type ReactNode } from 'react';
import { sv } from '@aldorock-fordon/shared/sv';

type Base = {
  id: string;
  label: string;
  help?: string | undefined;
  error?: string | undefined;
  required?: boolean | undefined;
};

function Wrap({ id, label, help, error, required, children }: Base & { children: ReactNode }) {
  return (
    <div className={error ? 'field is-invalid' : 'field'}>
      <label className="label" htmlFor={id}>
        {label}
        {required ? <span className="req"> ({sv.forms.requiredMark})</span> : null}
      </label>
      {help ? (
        <span className="help" id={`${id}-help`}>
          {help}
        </span>
      ) : null}
      {children}
      {error ? (
        <p className="error" id={`${id}-error`}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

const describedBy = (id: string, help?: string, error?: string) =>
  [help ? `${id}-help` : null, error ? `${id}-error` : null].filter(Boolean).join(' ') || undefined;

export function TextField(
  props: Base & {
    value: string;
    onChange: (v: string) => void;
    type?: 'text' | 'date' | 'number' | 'time';
    disabled?: boolean;
  },
) {
  return (
    <Wrap {...props}>
      <input
        className="input"
        id={props.id}
        name={props.id}
        type={props.type ?? 'text'}
        value={props.value}
        disabled={props.disabled}
        aria-invalid={props.error ? true : undefined}
        aria-required={props.required || undefined}
        aria-describedby={describedBy(props.id, props.help, props.error)}
        onChange={(e) => props.onChange(e.target.value)}
      />
    </Wrap>
  );
}

export function TextArea(
  props: Base & { value: string; onChange: (v: string) => void; disabled?: boolean; rows?: number },
) {
  return (
    <Wrap {...props}>
      <textarea
        className="textarea"
        id={props.id}
        name={props.id}
        rows={props.rows ?? 4}
        value={props.value}
        disabled={props.disabled}
        aria-invalid={props.error ? true : undefined}
        aria-required={props.required || undefined}
        aria-describedby={describedBy(props.id, props.help, props.error)}
        onChange={(e) => props.onChange(e.target.value)}
      />
    </Wrap>
  );
}

export function SelectField(
  props: Base & {
    value: string;
    onChange: (v: string) => void;
    options: Array<{ value: string; label: string }>;
    placeholder?: string;
    disabled?: boolean;
  },
) {
  return (
    <Wrap {...props}>
      <div className="select">
        <select
          id={props.id}
          name={props.id}
          value={props.value}
          disabled={props.disabled}
          aria-invalid={props.error ? true : undefined}
          aria-required={props.required || undefined}
          aria-describedby={describedBy(props.id, props.help, props.error)}
          onChange={(e) => props.onChange(e.target.value)}
        >
          <option value="">{props.placeholder ?? sv.forms.choose}</option>
          {props.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
    </Wrap>
  );
}

export function CheckboxField(
  props: Base & { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean },
) {
  return (
    <div className={props.error ? 'field is-invalid' : 'field'}>
      <label className="choice" htmlFor={props.id}>
        <input
          type="checkbox"
          id={props.id}
          name={props.id}
          checked={props.checked}
          disabled={props.disabled}
          aria-invalid={props.error ? true : undefined}
          aria-describedby={describedBy(props.id, props.help, props.error)}
          onChange={(e) => props.onChange(e.target.checked)}
        />
        <span>
          {props.label}
          {props.required ? <span className="req"> ({sv.forms.requiredMark})</span> : null}
        </span>
      </label>
      {props.help ? (
        <span className="help" id={`${props.id}-help`}>
          {props.help}
        </span>
      ) : null}
      {props.error ? (
        <p className="error" id={`${props.id}-error`}>
          {props.error}
        </p>
      ) : null}
    </div>
  );
}

/** The summary at the top of a form. Takes focus when it appears. */
export function ErrorSummary({ errors }: { errors: Array<{ id: string; message: string }> }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (errors.length) ref.current?.focus();
  }, [errors]);
  if (!errors.length) return null;
  return (
    <div className="error-summary" role="alert" tabIndex={-1} ref={ref}>
      <p className="notice" data-state="bad">
        {sv.forms.summary}
      </p>
      <ul className="squares">
        {errors.map((e) => (
          <li key={e.id}>
            <a href={`#${e.id}`}>{e.message}</a>
          </li>
        ))}
      </ul>
    </div>
  );
}
